import Reveal from '@/components/motion/Reveal'
import Skeleton from '@/components/ui/Skeleton'
import type { TradingSummary } from '@/features/trading-history/lib/tradingHistory'
import { compactRupees } from '@/lib/broker-format'
import { formatMoney, formatQuantity } from '@/lib/format'

const days = (count: number) => `${formatQuantity(count)} trading ${count === 1 ? 'day' : 'days'}`

interface Kpi {
  label: string
  value: string
  sub: string | undefined
}

/**
 * The three figures of a period, each in a card of its own (the mock's `mkc-kpi`): the price it
 * traded at on the whole, and what a day of it looked like. A figure whose source is missing
 * prints a dash and drops its line, never a zero.
 */
const kpisOf = (summary: TradingSummary): Kpi[] => [
  {
    label: 'Weighted avg price',
    value:
      summary.weightedAveragePrice === null
        ? '—'
        : `Rs ${formatMoney(summary.weightedAveragePrice)}`,
    sub: `Across ${days(summary.days)}`,
  },
  {
    label: 'Avg daily shares',
    value:
      summary.averageDailyShares === null
        ? '—'
        : `${formatQuantity(summary.averageDailyShares)} kitta`,
    sub:
      summary.totalShares === null
        ? undefined
        : `${formatQuantity(summary.totalShares)} shares total`,
  },
  {
    label: 'Avg daily turnover',
    value:
      summary.averageDailyTurnover === null
        ? '—'
        : `Rs ${compactRupees(summary.averageDailyTurnover)}`,
    sub:
      summary.totalTurnover === null
        ? undefined
        : `Rs ${compactRupees(summary.totalTurnover)} total`,
  },
]

const CARD =
  'flex min-w-0 flex-1 flex-col gap-2 border border-border bg-surface-sheet p-3 transition-colors hover:border-border-strong'

/** Three cards across (stacked on a narrow column): 16 between them at the mock's width. */
export default function AverageKpis({ summary }: { summary: TradingSummary }) {
  return (
    <div className="flex flex-col gap-2 @lg:flex-row @lg:gap-4">
      {kpisOf(summary).map((kpi) => (
        <div key={kpi.label} className={CARD}>
          <p className="text-h-xs text-muted-foreground">{kpi.label}</p>
          <Reveal as="p" variant="fade-up" className="text-h-section tnum">
            {kpi.value}
          </Reveal>
          <p className="text-body-xs text-muted-foreground">{kpi.sub ?? ' '}</p>
        </div>
      ))}
    </div>
  )
}

/** The loaded cards' boxes, so nothing moves when the figures land. */
export function AverageKpisSkeleton() {
  return (
    <div className="flex flex-col gap-2 @lg:flex-row @lg:gap-4">
      {[0, 1, 2].map((card) => (
        <div key={card} className={CARD}>
          <p className="text-h-xs text-muted-foreground">&nbsp;</p>
          <Skeleton className="h-7 w-32" />
          <p className="text-body-xs text-muted-foreground">&nbsp;</p>
        </div>
      ))}
    </div>
  )
}
